import { computed, watch } from 'vue';
import {
  useStore,
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

/**
 * VaultSage integration + inbox/conversation bot state for the active conversation.
 */
export function useVaultSageConversation() {
  const store = useStore();
  const { accountId, isCloudFeatureEnabled } = useAccount();
  const currentChat = useMapGetter('getSelectedChat');
  const integration = useFunctionGetter(
    'integrations/getIntegration',
    'vaultsage'
  );

  const isSageFeatureEnabled = computed(() =>
    isCloudFeatureEnabled(FEATURE_FLAGS.SAGE)
  );

  const vaultSageHook = computed(() => integration.value?.hooks?.[0] || null);

  const isVaultSageConnected = computed(() => {
    if (!isSageFeatureEnabled.value) return false;
    const hook = vaultSageHook.value;
    if (!hook) return false;
    if (hook.status === false) return false;
    return true;
  });

  const inboxVaultSageBot = computed(() => {
    const inboxId = currentChat.value?.inbox_id;
    if (!inboxId) return null;
    const bot = store.getters['agentBots/getActiveAgentBot'](inboxId);
    if (bot?.id && bot.bot_type === 'vaultsage') return bot;
    return null;
  });

  const assigneeBot = computed(() => {
    if (currentChat.value?.meta?.assignee_type !== 'AgentBot') return null;
    const assignee = currentChat.value?.meta?.assignee;
    if (!assignee || assignee.bot_type !== 'vaultsage') return null;
    return assignee;
  });

  const activeVaultSageBot = computed(
    () => assigneeBot.value || inboxVaultSageBot.value
  );

  const activeVaultSageBotName = computed(
    () => activeVaultSageBot.value?.name || ''
  );

  const hasActiveVaultSageBot = computed(() => !!activeVaultSageBot.value);

  const isVaultSagePreparing = computed(() => {
    if (currentChat.value?.meta?.assignee_type !== 'AgentBot') return false;
    if (currentChat.value?.meta?.assignee?.bot_type !== 'vaultsage')
      return false;
    return (
      currentChat.value?.status === 'pending' &&
      !!currentChat.value?.waiting_since
    );
  });

  const showVaultSageInWizardMenu = computed(
    () =>
      isSageFeatureEnabled.value &&
      isVaultSageConnected.value &&
      hasActiveVaultSageBot.value
  );

  const refreshVaultSageContext = () => {
    if (!isSageFeatureEnabled.value) return;
    store.dispatch('integrations/get');
    const inboxId = currentChat.value?.inbox_id;
    if (inboxId) {
      store.dispatch('agentBots/fetchAgentBotInbox', inboxId);
    }
  };

  watch(
    () => [accountId.value, currentChat.value?.inbox_id],
    () => {
      refreshVaultSageContext();
    },
    { immediate: true }
  );

  return {
    isSageFeatureEnabled,
    vaultSageHook,
    isVaultSageConnected,
    inboxVaultSageBot,
    assigneeBot,
    activeVaultSageBot,
    activeVaultSageBotName,
    hasActiveVaultSageBot,
    isVaultSagePreparing,
    showVaultSageInWizardMenu,
    refreshVaultSageContext,
  };
}
